// Template Designer — click on the PDF to place a field, drag to move,
// resize by the corner handle. Fields are stored as normalised (0..1)
// rectangles per page so the layout scales when the PDF is rendered
// at a different width in the signer view.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { BACKEND_URL } from "../api";
import { toast } from "sonner";
import { PdfCanvas } from "../components/documents/PdfCanvas";
import {
  ArrowLeft, ArrowRight, FloppyDisk, Trash, Plus, TextT, CheckSquare, Signature, Calendar, PencilSimple,
  CircleNotch,
} from "@phosphor-icons/react";

const FIELD_TYPES = [
  { id: "text", label: "Text", icon: TextT },
  { id: "checkbox", label: "Checkbox", icon: CheckSquare },
  { id: "signature", label: "Signature", icon: Signature },
  { id: "date", label: "Date", icon: Calendar },
];

const DEFAULT_SIZES = {
  text: { width: 0.28, height: 0.03 },
  checkbox: { width: 0.028, height: 0.022 },
  signature: { width: 0.32, height: 0.06 },
  date: { width: 0.14, height: 0.03 },
};

function newId() { return "f_" + Math.random().toString(36).slice(2, 9); }

export default function TemplateDesigner() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [fields, setFields] = useState([]);
  const [page, setPage] = useState(0);
  const [tool, setTool] = useState("text");
  const [pageSize, setPageSize] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging] = useState(null);   // {id, kind, startX, startY, rect}

  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/document-templates/${tid}`);
        setTpl(data);
        setFields((data.fields || []).map((f) => ({ ...f })));
      } catch { toast.error("Template not found"); nav("/admin/documents?tab=templates"); }
    })();
  }, [tid, nav]);

  const pageFields = fields.filter((f) => f.page === page);

  function placeField(e) {
    if (dragging) return;
    if (!pageSize) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - box.left) / box.width;
    const y = (e.clientY - box.top) / box.height;
    const size = DEFAULT_SIZES[tool];
    const name = window.prompt(`Name for this ${tool} field?`, "");
    if (!name) return;
    const f = {
      id: newId(),
      name,
      type: tool,
      page,
      x: Math.max(0, x - size.width / 2),
      y: Math.max(0, y - size.height / 2),
      width: size.width,
      height: size.height,
      required: tool === "signature",
    };
    setFields((all) => [...all, f]);
    setSelectedId(f.id);
  }

  function startDrag(e, id, kind) {
    e.stopPropagation();
    if (!pageSize) return;
    const f = fields.find((x) => x.id === id);
    if (!f) return;
    const box = canvasRef.current.getBoundingClientRect();
    setDragging({
      id, kind,
      startX: e.clientX / box.width,
      startY: e.clientY / box.height,
      rect: { x: f.x, y: f.y, width: f.width, height: f.height },
      boxW: box.width, boxH: box.height,
    });
    setSelectedId(id);
  }

  useEffect(() => {
    if (!dragging) return;
    const onMove = (ev) => {
      const nx = ev.clientX / dragging.boxW;
      const ny = ev.clientY / dragging.boxH;
      const dx = nx - dragging.startX;
      const dy = ny - dragging.startY;
      setFields((all) => all.map((f) => {
        if (f.id !== dragging.id) return f;
        if (dragging.kind === "move") {
          return { ...f, x: clamp(dragging.rect.x + dx, 0, 1 - f.width), y: clamp(dragging.rect.y + dy, 0, 1 - f.height) };
        }
        // resize
        return { ...f,
          width: clamp(dragging.rect.width + dx, 0.02, 1 - f.x),
          height: clamp(dragging.rect.height + dy, 0.015, 1 - f.y) };
      }));
    };
    const onUp = () => setDragging(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [dragging]);

  const removeField = (id) => setFields((all) => all.filter((f) => f.id !== id));
  const renameField = (id) => {
    const f = fields.find((x) => x.id === id); if (!f) return;
    const n = window.prompt("Field name", f.name); if (!n) return;
    setFields((all) => all.map((x) => x.id === id ? { ...x, name: n } : x));
  };
  const toggleRequired = (id) => setFields((all) => all.map((x) => x.id === id ? { ...x, required: !x.required } : x));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/document-templates/${tid}`, { fields });
      toast.success("Template saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  if (!tpl) return <div className="p-6 text-[#5C738A]"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  const pdfUrl = tpl.pdf_url.startsWith("http") ? tpl.pdf_url : `${BACKEND_URL}${tpl.pdf_url}`;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="template-designer">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2 flex-shrink-0">
        <div>
          <button onClick={() => nav("/admin/documents?tab=templates")} data-testid="back-templates" className="text-xs text-[#5C738A] hover:text-[#012340] inline-flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Templates</button>
          <h1 className="font-heading text-2xl font-black text-[#012340] mt-1">{tpl.name}</h1>
          <div className="text-xs text-[#5C738A]">{fields.length} field{fields.length === 1 ? "" : "s"} mapped · {tpl.page_count} pages</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FIELD_TYPES.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`tool-${t.id}`} className={`px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2 ${tool === t.id ? "bg-[#012340] text-white" : "bg-white border border-[#012340]/15 text-[#5C738A]"}`}>
              <t.icon size={14} weight="bold" /> {t.label}
            </button>
          ))}
          <button onClick={save} disabled={saving} data-testid="save-template" className="px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center gap-2 disabled:opacity-50">
            <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <button disabled={page <= 0} onClick={() => setPage((p) => p - 1)} className="text-xs text-[#5C738A] disabled:opacity-30 inline-flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Prev</button>
        <div className="text-xs text-[#5C738A]">Page {page + 1} of {tpl.page_count}</div>
        <button disabled={page >= tpl.page_count - 1} onClick={() => setPage((p) => p + 1)} className="text-xs text-[#5C738A] disabled:opacity-30 inline-flex items-center gap-1">Next <ArrowRight size={12} weight="bold" /></button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 bg-[#f4f5f7] rounded-sm" ref={canvasRef}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div onClick={placeField} style={{ cursor: "crosshair" }}>
            <PdfCanvas
              file={pdfUrl}
              pageIndex={page}
              width={900}
              onSize={setPageSize}
            >
              {(ps) => (
                <div style={{ position: "absolute", inset: 0, pointerEvents: "auto" }}>
                  {pageFields.map((f) => {
                    const active = f.id === selectedId;
                    return (
                      <div
                        key={f.id}
                        data-testid={`field-box-${f.id}`}
                        onMouseDown={(e) => startDrag(e, f.id, "move")}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          left: f.x * ps.width,
                          top: f.y * ps.height,
                          width: f.width * ps.width,
                          height: f.height * ps.height,
                          border: `2px solid ${active ? "#CFAB7A" : "#012340"}`,
                          background: active ? "rgba(207,171,122,0.18)" : "rgba(1,35,64,0.10)",
                          cursor: "move",
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#012340",
                          padding: 2,
                        }}
                      >
                        <div style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.7 }}>{f.type}{f.required ? " *" : ""}</div>
                        <div style={{ fontSize: 11, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                        {active && (
                          <div style={{ position: "absolute", right: -2, bottom: -2, width: 12, height: 12, background: "#CFAB7A", cursor: "nwse-resize" }} onMouseDown={(e) => startDrag(e, f.id, "resize")} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </PdfCanvas>
          </div>
        </div>
      </div>

      {selectedId && (() => {
        const f = fields.find((x) => x.id === selectedId);
        if (!f) return null;
        return (
          <div className="fixed bottom-4 right-4 bg-white border border-[#012340]/15 rounded-sm p-3 shadow-lg z-50 text-sm" data-testid="selected-field">
            <div className="font-heading font-black text-[#012340]">{f.name}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C738A]">{f.type} · page {f.page + 1}</div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => renameField(f.id)} className="px-3 py-1 border border-[#012340]/15 rounded-sm text-xs inline-flex items-center gap-1"><PencilSimple size={12} weight="bold" /> Rename</button>
              <button onClick={() => toggleRequired(f.id)} className={`px-3 py-1 rounded-sm text-xs ${f.required ? "bg-[#CFAB7A] text-[#012340]" : "border border-[#012340]/15"}`}>{f.required ? "Required" : "Optional"}</button>
              <button onClick={() => { removeField(f.id); setSelectedId(null); }} className="px-3 py-1 border border-[#b23]/40 text-[#b23] rounded-sm text-xs inline-flex items-center gap-1"><Trash size={12} weight="bold" /> Delete</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
